import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getSubscriptions, UnauthorizedError } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import { useNotifs } from '../../utils/useNotif';
import { stubSubscriptionItems } from '../subsStubData';
import Row from './Row';
import { wrapDynamicBabItems } from '../../utils/BundleUtils';

const DEMO_SEARCH_PARAM = 'demo';

const useSubscriptions = (
  customerId: string | undefined,
  authToken: string | undefined,
  logout: () => void,
): [Subscriptions | undefined, boolean, string | undefined] => {
  const [subscriptions, setSubscriptions] = useState<
    Subscriptions | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let subscriptions;

      // Get subscriptions or mock data
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has(DEMO_SEARCH_PARAM)) {
        subscriptions = stubSubscriptionItems(
          searchParams.get(DEMO_SEARCH_PARAM) || 'none',
        );
      } else if (authToken) {
        subscriptions = await getSubscriptions(customerId, authToken, true);
      }

      // sort subs by nextbilling date and status
      subscriptions?.Items?.sort((a, b) => {
        const penalty = dayjs().unix();
        const dateA = dayjs(a.NextBillingDate).unix();
        const penaltyA = a.SubscriptionStatus !== 'ACTIVE' ? penalty : 0;
        const dateB = dayjs(b.NextBillingDate).unix();
        const penaltyB = b.SubscriptionStatus !== 'ACTIVE' ? penalty : 0;

        return dateA + penaltyA - (dateB + penaltyB);
      });

      setSubscriptions(subscriptions);
    } catch (err: any) {
      console.log(err);
      setError(err);

      if (err instanceof UnauthorizedError) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [customerId, authToken]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return [subscriptions, loading, error];
};

export default (): React.ReactElement => {
  const { t } = useTranslation();
  const {
    isShopifyLoggedIn,
    customerId,
    authToken,
    logout,
  } = useGlobalContext();

  const [subscriptions, loading, error] = useSubscriptions(
    customerId,
    authToken,
    logout,
  );

  const { renderNotifs } = useNotifs();

  return (
    <Container>
      {renderNotifs()}

      <Title className="bundleapp-subscriptions-list__title">
        {t('titles.my_subscriptions')}
      </Title>

      {!isShopifyLoggedIn ? (
        <Logout onClick={logout}>{t('titles.logout')}</Logout>
      ) : null}

      {!authToken ? (
        <CenteredParagraph>{t('subscriptions.no_subs')}</CenteredParagraph>
      ) : null}

      {loading ? <p>{t('titles.loading')}</p> : null}

      {error ? <p>{t('subscriptions.error')}</p> : null}

      {!loading && subscriptions ? (
        <ListContainer className="bundleapp-subscriptions-list__container">
          {subscriptions?.Items?.map((subscription) => {
            return (
              <Row
                key={subscription.SubscriptionId}
                subscription={wrapDynamicBabItems(subscription)}
              />
            );
          })}
        </ListContainer>
      ) : null}
    </Container>
  );
};

const Title = styled.h2`
  text-align: center;
`;
const CenteredParagraph = styled.p`
  text-align: center;
`;

const ListContainer = styled.div`
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: stretch;
  align-content: space-between;
  margin-top: 52px;

  div:empty {
    display: inline-block;
  }
`;

const Container = styled.div`
  max-width: 930px;
  margin: 0 auto 40px;
`;

const Logout = styled.button`
  margin: 5px auto;
  text-align: center;
  font-size: 0.9em;
  display: block;
  font-weight: 400;
  background: none;
  text-align: center;
  box-sizing: border-box;
  border: none;
`;
